import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import * as Icon from 'ubs-icons';
import { addModal, getRandomId, getTopModal, removeModal } from '../common/function';
import { COLOR } from '../common/constant';

const ITEM_LABEL_KEYS = ['name', 'label', 'title'];

// Mirrors the 0.18s opacity / transform transition on `.u_cmdp-overlay` and `.u_cmdp-panel`.
const TRANSITION_MS = 180;

const defaultRenderItem = (item) => {
    if (item === null || item === undefined) return null;
    if (typeof item === 'string' || typeof item === 'number') return item;
    const key = ITEM_LABEL_KEYS.find(k => item?.[k] !== undefined && item?.[k] !== null);
    return key ? item[key] : null;
};

const UCommandPalette = (props) => {
    const {
        open, onClose, groups, search, onSearch, onDebouncedSearch, debounce,
        placeholder, emptyText, loadingText, loading, footer, maxWidth, zIndex,
        className, escHint, closeOnSelect, closeOnOutsideClick, onSelect, resetOnOpen,
        children, onKeyDown,
    } = props;

    const inputRef = useRef(null);
    const activeRef = useRef(null);
    const modalIdRef = useRef(getRandomId());
    const debounceRef = useRef(null);
    const lastSentRef = useRef(null);

    const [mounted, setMounted] = useState(false);
    // Kept true for the length of the exit transition so the panel can animate out instead of
    // being torn out of the DOM the moment `open` flips.
    const [visible, setVisible] = useState(false);
    const [value, setValue] = useState(search || '');
    const [activeIndex, setActiveIndex] = useState(0);

    const { sections, flatItems } = useMemo(() => {
        const flat = [];
        const built = [];
        (groups || []).forEach((group, groupIndex) => {
            if (!group) return;
            const all = Array.isArray(group?.items) ? group.items : [];
            const items = group?.limit ? all.slice(0, group.limit) : all;
            if (!items.length) return;

            const entries = items.map((item, itemIndex) => {
                const entry = { item, group, itemIndex, index: flat.length };
                flat.push(entry);
                return entry;
            });
            built.push({ group, entries, key: group?.key ?? groupIndex });
        });
        return { sections: built, flatItems: flat };
    }, [groups]);

    useEffect(() => {
        if (!open) return undefined;
        const id = modalIdRef.current;
        addModal(id);
        return () => removeModal(id);
    }, [open]);

    useEffect(() => {
        if (typeof search === 'string' && search !== value) setValue(search);
    }, [search]);

    useEffect(() => {
        if (!open) {
            // Dropping `u_cmdp-open` runs the same transition backwards, then the node goes.
            setMounted(false);
            setActiveIndex(0);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            lastSentRef.current = null;

            const unmount = setTimeout(() => setVisible(false), TRANSITION_MS);
            return () => clearTimeout(unmount);
        }

        setVisible(true);
        if (resetOnOpen) setValue('');
        const paint = setTimeout(() => setMounted(true), 10);
        const focus = setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
        }, 60);

        return () => {
            clearTimeout(paint);
            clearTimeout(focus);
        };
    }, [open, resetOnOpen]);

    useEffect(() => {
        setActiveIndex(prev => (prev >= flatItems.length ? 0 : prev));
    }, [flatItems.length]);

    useEffect(() => {
        activeRef.current?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    const close = useCallback(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        onClose?.();
    }, [onClose]);

    useEffect(() => {
        if (!open) return undefined;
        const handler = (event) => {
            if (event.keyCode !== 27) return;
            if (getTopModal() !== modalIdRef.current) return;
            event.preventDefault();
            event.stopPropagation();
            close();
        };
        document.addEventListener('keydown', handler, true);
        return () => document.removeEventListener('keydown', handler, true);
    }, [open, close]);

    const commit = (entry) => {
        if (!entry) return;
        if (closeOnSelect) close();
        if (typeof entry.group?.onSelect === 'function') entry.group.onSelect(entry.item, entry.itemIndex);
        else onSelect?.(entry.item, entry.group);
    };

    const move = (step) => {
        if (!flatItems.length) return;
        setActiveIndex(prev => {
            const next = prev + step;
            if (next < 0) return flatItems.length - 1;
            if (next >= flatItems.length) return 0;
            return next;
        });
    };

    const handleChange = (event) => {
        const next = event.target.value;
        setValue(next);
        setActiveIndex(0);
        onSearch?.(next);

        if (!onDebouncedSearch) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (lastSentRef.current === next) return;
            lastSentRef.current = next;
            onDebouncedSearch(next);
        }, debounce);
    };

    const handleKeyDown = (event) => {
        onKeyDown?.(event);

        // With custom body content the consumer owns arrow / enter behaviour.
        if (children) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                move(1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                move(-1);
                break;
            case 'Enter':
                event.preventDefault();
                commit(flatItems[activeIndex]);
                break;
            case 'Home':
                if (!value) { event.preventDefault(); setActiveIndex(0); }
                break;
            case 'End':
                if (!value && flatItems.length) { event.preventDefault(); setActiveIndex(flatItems.length - 1); }
                break;
            default:
                break;
        }
    };

    const clear = () => {
        setValue('');
        setActiveIndex(0);
        onSearch?.('');
        if (debounceRef.current) clearTimeout(debounceRef.current);
        lastSentRef.current = '';
        onDebouncedSearch?.('');
        inputRef.current?.focus();
    };

    // `open` alone would unmount instantly; `visible` keeps it around while it animates out.
    if (!open && !visible) return null;

    const palette = (
        <div
            className={`u_cmdp ${mounted ? 'u_cmdp-open' : ''} ${className || ''}`}
            style={{ zIndex }}
            role="dialog"
            aria-modal="true"
            aria-label={placeholder}
        >
            <div className="u_cmdp-overlay" onClick={() => closeOnOutsideClick && close()} />

            <div className="u_cmdp-panel" style={{ maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth }}>

                <div className="u_cmdp-search">
                    <span className="u_cmdp-search-icon"><Icon.Search size={16} fill={COLOR?.u_blue} /></span>
                    <input
                        ref={inputRef}
                        type="text"
                        className="u_cmdp-input"
                        placeholder={placeholder}
                        value={value}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        autoComplete="off"
                        aria-autocomplete="list"
                        aria-controls="u_cmdp-results"
                    />
                    {value?.length > 0 && (
                        <span className="u_cmdp-clear u_cp" onClick={clear} role="button" aria-label="Clear search">
                            <Icon.Close size={12} fill={COLOR?.u_icon_bg} />
                        </span>
                    )}
                    {escHint && <span className="u_cmdp-kbd" onClick={close} role="button" aria-label="Close">{escHint}</span>}
                </div>

                <div className="u_cmdp-body" id="u_cmdp-results" role={children ? undefined : 'listbox'}>
                    {children}

                    {!children && loading && <div className="u_cmdp-state">{loadingText}</div>}

                    {!children && !loading && !flatItems.length && <div className="u_cmdp-state">{emptyText}</div>}

                    {!children && !loading && sections.map(section => (
                        <div className="u_cmdp-group" key={section.key}>
                            {section.group?.label && (
                                <div className="u_cmdp-group-label"><span>{section.group.label}</span></div>
                            )}
                            <ul className="u_cmdp-list">
                                {section.entries.map(entry => {
                                    const { item, itemIndex, index } = entry;
                                    const group = section.group;
                                    const isActive = index === activeIndex;
                                    const render = group?.renderItem || defaultRenderItem;
                                    const badge = group?.renderBadge
                                        ? group.renderBadge(item, itemIndex)
                                        : (group?.badge ? <span className="u_cmdp-badge">{group.badge}</span> : null);

                                    return (
                                        <li
                                            key={group?.itemKey ? group.itemKey(item, itemIndex) : (item?._id ?? `${section.key}-${itemIndex}`)}
                                            ref={isActive ? activeRef : null}
                                            className={`u_cmdp-item ${isActive ? 'active' : ''} ${group?.itemClassName || ''}`}
                                            role="option"
                                            aria-selected={isActive}
                                            onMouseMove={() => !isActive && setActiveIndex(index)}
                                            onClick={() => commit(entry)}
                                        >
                                            <div className="u_cmdp-item-main">{render(item, itemIndex)}</div>
                                            {badge && <div className="u_cmdp-item-aside">{badge}</div>}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>

                {footer && <div className="u_cmdp-footer">{footer}</div>}
            </div>
        </div>
    );

    if (typeof document === 'undefined' || !document.body) return palette;
    return ReactDOM.createPortal(palette, document.body);
};

UCommandPalette.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    groups: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.node,
        items: PropTypes.array,
        limit: PropTypes.number,
        itemKey: PropTypes.func,
        renderItem: PropTypes.func,
        badge: PropTypes.node,
        renderBadge: PropTypes.func,
        itemClassName: PropTypes.string,
        onSelect: PropTypes.func,
    })),
    search: PropTypes.string,
    onSearch: PropTypes.func,
    onDebouncedSearch: PropTypes.func,
    debounce: PropTypes.number,
    onSelect: PropTypes.func,
    placeholder: PropTypes.string,
    emptyText: PropTypes.node,
    loadingText: PropTypes.node,
    loading: PropTypes.bool,
    footer: PropTypes.node,
    maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    zIndex: PropTypes.number,
    className: PropTypes.string,
    escHint: PropTypes.node,
    closeOnSelect: PropTypes.bool,
    closeOnOutsideClick: PropTypes.bool,
    resetOnOpen: PropTypes.bool,
    children: PropTypes.node,
    onKeyDown: PropTypes.func,
};

UCommandPalette.defaultProps = {
    open: false,
    groups: [],
    search: undefined,
    debounce: 350,
    placeholder: 'Search...',
    emptyText: 'No result found.',
    loadingText: 'Searching...',
    loading: false,
    footer: null,
    maxWidth: 640,
    zIndex: 10000010,
    escHint: 'esc',
    closeOnSelect: true,
    closeOnOutsideClick: true,
    resetOnOpen: false,
};

export default UCommandPalette;
